// ===========================================================================
// Registry de rutas de la app.
//
// CONVENCIÓN: cada módulo de src/modules/<nombre>/ publica sus rutas desde su
// index.js como `export const <nombre>Routes = [{ path, element }]`. Este
// archivo solo las concatena — no importa componentes sueltos, y AppRouter no
// conoce ningún módulo por dentro.
//
// Reglas:
//  - El módulo resuelve su propio gating por feature flag: si está deshabilitado
//    exporta `[]` (ver playground, coding-opportunities-v2). Nada de condicionales
//    acá ni en AppRouter.
//  - El módulo declara sus propios alias y redirects (ver care-gaps-v2).
//  - `noRoleGuard: true` en una ruta la exime del RoleGuard (solo dev/playground).
//
// Para agregar un módulo: exportá su array desde su index.js e insertalo abajo.
// ===========================================================================
import { Navigate } from "react-router-dom";

// Público (sin sesión)
import { loginV2Routes } from "../modules/login-v2";
import { AccessDeniedPage } from "./AccessDenied";
import Unauthorized from "./Unauthorized/Unauthorized";
import MfaRequiredPage from "./MfaRequired/MfaRequiredPage";

// Protegido — dentro del Layout
import { mainRoutes } from "../modules/Main";
import { carePlansRoutes } from "../modules/care-plans";
import { assignmentRoutes } from "../modules/assignments";
import { assignmentLegacyRoutes } from "../modules/assignment";
import { clinicalInfoRoutes } from "../modules/clinical-info";
import { careManagementSystemRoutes } from "../modules/care-management-system";
import { patientMapRoutes } from "../modules/patient-map";
import { toolsRoutes } from "../modules/tools";
import { clinicalDataCollectorRoutes } from "../modules/clinical-data-collector";
import { labPhlebotomyRoutes } from "../modules/lab-phlebotomy";
import { specialistReferralsRoutes } from "../modules/specialist-referrals";
import { userProfileRoutes } from "../modules/user-profile";
import { dataQualityRoutes } from "../modules/data-quality";
import { medicationReconciliationRoutes } from "../modules/medication-reconciliation";
import { patientGeographyRoutes } from "../modules/patient-geography";

// Notas clínicas (tres generaciones conviviendo — ver docs/modulos)
import { clinicalNotesListRoutes } from "../modules/clinical-notes-list";
import { createClinicalNoteRoutes } from "../modules/create-clinical-note";
import { clinicalNotesRoutes } from "../modules/clinical-notes";
import { clinicalNotesV2Routes } from "../modules/clinical-notes-v2";
import { clinicalNoteAiRoutes } from "../modules/clinical-note-ai";
// TEMPORAL — revisión de los templates de nota clínica (borrar al cerrarla).
import { clinicalNoteTemplateConfirmRoutes } from "../modules/clinical-note-template-confirm";

// Care Gaps / Coding
import { careGapsV2Routes } from "../modules/care-gaps-v2";
import { careGapsLegacyRoutes } from "../modules/care-gaps";
import { codingOpportunitiesRoutes } from "../modules/coding-opportunities";
import { codingOpportunitiesV2Routes } from "../modules/coding-opportunities-v2";

// Etapa 2 — BillingV2
import { stage2GuideRoutes } from "../modules/stage2-guide";
import { chargeCaptureRoutes } from "../modules/charge-capture";
import { cdciRoutes } from "../modules/cdci";
import { clinicalNotesTranscriptorRoutes } from "../modules/clinical-notes-transcriptor";
import { postDischargeRoutes } from "../modules/post-discharge";
import { highRiskSurveillanceRoutes } from "../modules/high-risk-surveillance";
import { medicationAdherenceRoutes } from "../modules/medication-adherence";
import { memberOutreachRoutes } from "../modules/member-outreach";
import { memberOnboardingRoutes } from "../modules/member-onboarding";
import { previsitPlanningRoutes } from "../modules/previsit-planning";
import { providerDocCoachRoutes } from "../modules/provider-doc-coach";
import { denialsManagementRoutes } from "../modules/denials-management";
import { membershipAnalyticsRoutes } from "../modules/membership-analytics";
import { executiveFinancialRoutes } from "../modules/executive-financial";
import { billingDashboardRoutes } from "../modules/billing-dashboard";
import { clinicalOperationsRoutes } from "../modules/clinical-operations";
import { providerPerformanceRoutes } from "../modules/provider-performance";

// Transversales
import { generalRoutes } from "../modules/general";
import { tasksRoutes } from "../modules/tasks";
import { technicalIssuesRoutes } from "../modules/technical-issues";
import { libraryRoutes } from "../modules/library";
import { highCostRoutes } from "../modules/high-cost";
import { noRecentVisitRoutes } from "../modules/no-recent-visit";
import { administrationRoutes } from "../modules/administration";
import { supportRoutes } from "../modules/support";
import { playgroundRoutes } from "../modules/playground";

// Contact Center
import { callCenterIntakeRoutes } from "../modules/call-center-intake";
import { transportationRoutes } from "../modules/transportation";
import { newMembersRoutes } from "../modules/new-members";
import { inactivesRoutes } from "../modules/inactives";
import { callRecentVisitRoutes } from "../modules/call-recent-visit";
import { welcomeOepRoutes } from "../modules/welcome-oep";
import { callAssignmentsRoutes } from "../modules/call-assignments";

// Membership
import { membershipRoutes } from "../modules/membership";

// Cash
import { cashRoutes } from "../modules/cash";

/** Rutas accesibles sin sesión. */
export const publicRoutes = [
  ...loginV2Routes,
  // Alias histórico de /auth/change-password.
  {
    path: "/ChangePassword",
    element: loginV2Routes.find((r) => r.path === "/auth/change-password")
      ?.element,
  },
  { path: "/Unauthorized", element: <Unauthorized /> },
  { path: "/AccessDenied", element: <AccessDeniedPage /> },
  // Bloqueo HIPAA: usuarios sin 2FA aterrizan acá desde ProtectedRoute y no
  // pueden entrar al resto de la app hasta completar el enrollment. La propia
  // página maneja sus guards (no-user → /Login, mfaEnabled → /Main).
  { path: "/mfa-required", element: <MfaRequiredPage /> },
];

/** Rutas con sesión, dentro del Layout. Todas pasan por RoleGuard salvo noRoleGuard. */
export const protectedRoutes = [
  { path: "/", element: <Navigate to="/Main" replace />, noRoleGuard: true },

  ...mainRoutes,
  ...carePlansRoutes,
  ...assignmentRoutes,
  ...assignmentLegacyRoutes,
  ...clinicalInfoRoutes,
  ...careManagementSystemRoutes,
  ...patientMapRoutes,
  ...toolsRoutes,
  ...clinicalDataCollectorRoutes,
  ...labPhlebotomyRoutes,
  ...specialistReferralsRoutes,
  ...userProfileRoutes,
  ...dataQualityRoutes,
  ...medicationReconciliationRoutes,
  ...patientGeographyRoutes,

  ...clinicalNotesListRoutes,
  ...createClinicalNoteRoutes,
  ...clinicalNotesRoutes,
  ...clinicalNotesV2Routes,
  ...clinicalNoteAiRoutes,
  ...clinicalNoteTemplateConfirmRoutes,

  ...careGapsV2Routes,
  ...careGapsLegacyRoutes,
  ...codingOpportunitiesRoutes,
  ...codingOpportunitiesV2Routes,

  ...stage2GuideRoutes,
  ...chargeCaptureRoutes,
  ...cdciRoutes,
  ...clinicalNotesTranscriptorRoutes,
  ...postDischargeRoutes,
  ...highRiskSurveillanceRoutes,
  ...medicationAdherenceRoutes,
  ...memberOutreachRoutes,
  ...memberOnboardingRoutes,
  ...previsitPlanningRoutes,
  ...providerDocCoachRoutes,
  ...denialsManagementRoutes,
  ...membershipAnalyticsRoutes,
  ...executiveFinancialRoutes,
  ...billingDashboardRoutes,
  ...clinicalOperationsRoutes,
  ...providerPerformanceRoutes,

  ...generalRoutes,
  ...tasksRoutes,
  ...technicalIssuesRoutes,
  ...libraryRoutes,
  ...highCostRoutes,
  ...noRecentVisitRoutes,
  ...administrationRoutes,
  ...supportRoutes,
  ...playgroundRoutes,

  ...callCenterIntakeRoutes,
  ...transportationRoutes,
  ...newMembersRoutes,
  ...inactivesRoutes,
  ...callRecentVisitRoutes,
  ...welcomeOepRoutes,
  ...callAssignmentsRoutes,

  ...membershipRoutes,
  ...cashRoutes,
];
